import { createTheme, alpha } from '@mui/material/styles';

// Trindent official brand colors from palette
export const modernColors = {
  // Primary blue from Trindent branding
  primary: {
    50: '#E6F0F7',
    100: '#CCE1EF',
    200: '#99C3DF',
    300: '#66A5CF',
    400: '#3387BF',
    500: '#0069AA', // Official Trindent Blue (RGB: 0, 105, 170)
    600: '#005488',
    700: '#003F66',
    800: '#002A44',
    900: '#001522',
  },
  // Neutral grays for clean interface
  neutral: {
    50: '#FAFBFC',
    100: '#F6F8FA',
    200: '#E1E7EC',
    300: '#D1D9E0',
    400: '#B8C4CE',
    500: '#8B9AAB',
    600: '#6B7C8F',
    700: '#4A5568',
    800: '#2D3748',
    900: '#1A202C',
  },
  // Secondary colors from Trindent palette
  secondary: {
    50: '#F3F5F9',
    100: '#D9E2F3', // Light Blue (RGB: 217, 226, 243)
    200: '#B4C6E7', // Light Blue (RGB: 180, 198, 231)
    300: '#8EAADB', // Medium Blue (RGB: 142, 170, 219)
    400: '#6991C7',
    500: '#2F5496', // Dark Blue (RGB: 47, 84, 150)
    600: '#254377',
    700: '#1B3258',
    800: '#112139',
    900: '#002060', // Dark Navy (RGB: 0, 32, 96)
  },
  // Chart colors for data visualization
  chartColors: {
    series1: '#0069AA', // Trindent Blue
    series2: '#2F5496', // Dark Blue
    series3: '#8EAADB', // Medium Blue
    series4: '#B4C6E7', // Light Blue
    series5: '#002060', // Dark Navy
    series6: '#D9E2F3', // Lightest Blue
  },
  // Status colors (keeping existing for consistency)
  success: '#00B050', // Green from palette
  warning: '#FFC107', // Yellow/amber for warning (standard traffic light yellow)
  error: '#FF0000',   // Red from palette
  info: '#0069AA',    // Trindent Blue for info
};

export const createModernTheme = (mode: 'light' | 'dark', fontScale: number = 1) => {
  const isDark = mode === 'dark';

  return createTheme({
    // Scale spacing (padding/margins) with the text so elements grow together, and
    // scale breakpoints UP so responsive grids reflow to fewer columns at larger
    // sizes (the app registers as a "smaller" viewport). Combined with the root
    // font-size scaling (rem text), the whole UI grows and reflows without overflow.
    spacing: 8 * fontScale,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600 * fontScale,
        md: 900 * fontScale,
        lg: 1200 * fontScale,
        xl: 1536 * fontScale,
      },
    },
    palette: {
      mode,
      primary: {
        main: modernColors.primary[500],
        light: modernColors.primary[300],
        dark: modernColors.primary[700],
      },
      secondary: {
        main: modernColors.secondary[500],
        light: modernColors.secondary[300],
        dark: modernColors.secondary[700],
      },
      background: {
        default: isDark ? '#0A0E27' : '#FFFFFF',
        paper: isDark ? '#0F1629' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#FFFFFF' : modernColors.neutral[900],
        secondary: isDark ? modernColors.neutral[400] : modernColors.neutral[600],
      },
      divider: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
      success: {
        main: modernColors.success,
        light: alpha(modernColors.success, 0.3),
        dark: alpha(modernColors.success, 0.7),
      },
      warning: {
        main: modernColors.warning,
        light: alpha(modernColors.warning, 0.3),
        dark: alpha(modernColors.warning, 0.7),
      },
      error: {
        main: modernColors.error,
        light: alpha(modernColors.error, 0.3),
        dark: alpha(modernColors.error, 0.7),
      },
      info: {
        main: modernColors.info,
        light: alpha(modernColors.info, 0.3),
        dark: alpha(modernColors.info, 0.7),
      },
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '1rem',
        lineHeight: 1.75,
        letterSpacing: '0.00938em',
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
      },
      body1: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
      },
      body2: {
        fontSize: '0.8125rem',
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.66,
        letterSpacing: '0.03333em',
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      isDark ? '0 1px 3px 0 rgba(0, 0, 0, 0.5)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      isDark ? '0 10px 15px -3px rgba(0, 0, 0, 0.5)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      isDark ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)' : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      ...Array(20).fill(isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'),
    ] as any,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: isDark ? modernColors.neutral[800] : modernColors.neutral[100],
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? modernColors.neutral[600] : modernColors.neutral[400],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? modernColors.neutral[500] : modernColors.neutral[500],
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            padding: '8px 16px',
            transition: 'all 0.2s ease',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isDark ? '0 4px 12px rgba(0, 105, 170, 0.4)' : '0 4px 12px rgba(0, 105, 170, 0.2)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderColor: isDark ? alpha('#FFFFFF', 0.12) : alpha('#000000', 0.12),
            '&:hover': {
              backgroundColor: isDark ? alpha('#FFFFFF', 0.04) : alpha('#000000', 0.04),
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: isDark ? '#0F1629' : '#FFFFFF',
            borderRadius: '12px',
            border: `1px solid ${isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08)}`,
          },
          elevation1: {
            boxShadow: isDark
              ? '0 1px 3px 0 rgba(0, 0, 0, 0.5)'
              : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          },
          elevation2: {
            boxShadow: isDark
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0F1629' : '#FFFFFF',
            borderBottom: `1px solid ${isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08)}`,
            color: isDark ? '#FFFFFF' : modernColors.neutral[900],
            backdropFilter: 'blur(8px)',
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0F1629' : '#FFFFFF',
            borderRight: `1px solid ${isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08)}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08)}`,
          },
          head: {
            fontWeight: 600,
            backgroundColor: isDark ? alpha('#FFFFFF', 0.02) : alpha('#000000', 0.02),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            fontWeight: 500,
            fontSize: '0.75rem',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: isDark ? alpha('#FFFFFF', 0.12) : alpha('#000000', 0.12),
              },
              '&:hover fieldset': {
                borderColor: isDark ? alpha('#FFFFFF', 0.24) : alpha('#000000', 0.24),
              },
              '&.Mui-focused fieldset': {
                borderColor: modernColors.primary[500],
              },
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: `1px solid ${isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08)}`,
            boxShadow: isDark
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            backgroundImage: 'none',
            backgroundColor: isDark ? '#0F1629' : '#FFFFFF',
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            backgroundColor: isDark ? alpha('#FFFFFF', 0.08) : alpha('#000000', 0.08),
          },
        },
      },
    },
  });
};